import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message
} from 'antd'
import PageTitle from 'components/PageTitle'
import {
  deleteRequest,
  getRequest,
  growthPlaceUrl,
  growthPlacesUrl,
  patchRequest,
  postRequest
} from '../../config'
import { countryOptions, flag, splitPlaces } from './utils'

const { Text } = Typography
const COUNTRIES = countryOptions()

// Places in reach (spec: 0042_growth_bot Req 2a). Events in reach are sent whether or not they're
// sponsored; anywhere else needs a funded route.
const PlacesPage = () => {
  const [places, setPlaces] = useState([])
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState(null) // null, {} for a new place, or a place to edit
  const [form] = Form.useForm()

  const load = async () => {
    const response = await getRequest(growthPlacesUrl, 'places', setLoading)
    if (response.success) setPlaces(response.data || [])
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    if (!modal) return
    form.resetFields()
    form.setFieldsValue(modal.id ? modal : { reachable: true, city: '', note: '' })
  }, [modal, form])

  const errorOf = response => {
    const data = response.error?.response?.data || {}
    return data.detail || data.city || data.countryCode || data.country_code
  }

  const save = async () => {
    const values = await form.validateFields()
    const payload = { ...values, city: (values.city || '').trim(), note: (values.note || '').trim() }
    const response = modal.id
      ? await patchRequest(growthPlaceUrl(modal.id), payload, 'place', () => {})
      : await postRequest(growthPlacesUrl, payload, 'place', () => {})
    if (response.success) {
      setModal(null)
      load()
    } else if (errorOf(response)) {
      message.error(String(errorOf(response)))
    }
  }

  const move = async place => {
    const response = await patchRequest(growthPlaceUrl(place.id), { reachable: !place.reachable }, 'place', () => {})
    if (response.success) load()
  }

  const remove = async place => {
    const response = await deleteRequest(growthPlaceUrl(place.id), 'place', () => {})
    if (response.success) load()
  }

  const addEu = async () => {
    const response = await postRequest(growthPlacesUrl, { group: 'eu' }, 'EU countries', () => {})
    if (response.success) {
      message.success(response.data.added ? `Added ${response.data.added} countries.` : 'Every EU country is already listed.')
      load()
    }
  }

  const { inReach, outOfReach } = splitPlaces(places)

  const renderList = (title, items, toLabel) => (
    <Card title={`${title} (${items.length})`} size="small">
      <List
        loading={loading}
        dataSource={items}
        locale={{ emptyText: <Empty description="None yet" /> }}
        renderItem={place => (
          <List.Item
            actions={[
              <Button key="move" size="small" onClick={() => move(place)}>
                {toLabel}
              </Button>,
              <Button key="edit" size="small" type="link" onClick={() => setModal(place)}>
                Edit
              </Button>,
              <Popconfirm key="remove" title="Remove this place?" onConfirm={() => remove(place)}>
                <Button size="small" type="link" danger>
                  Remove
                </Button>
              </Popconfirm>
            ]}
          >
            <Space direction="vertical" size={0}>
              <span>
                {flag(place.countryCode)} {place.city ? `${place.city}, ` : ''}
                {place.countryName}
              </span>
              {place.overrides ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Overrides {place.countryName}: {place.overrides}
                </Text>
              ) : null}
              {place.city && place.matches === 0 ? (
                <Tag color="orange">No events matched yet; check the spelling</Tag>
              ) : null}
              {place.note ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {place.note}
                </Text>
              ) : null}
            </Space>
          </List.Item>
        )}
      />
    </Card>
  )

  return (
    <div className="container-fluid">
      <PageTitle title="Growth · Places in reach" />
      <Card
        title="Places in reach"
        extra={
          <Space wrap>
            <Button onClick={addEu}>Add EU countries</Button>
            <Button type="primary" onClick={() => setModal({})}>
              Add place
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Events in places in reach are sent whether or not they're sponsored. Anywhere else needs a funded route. Places not listed count as out of reach. Changes apply from the next digest."
        />
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            {renderList('In reach', inReach, 'Move out')}
          </Col>
          <Col xs={24} md={12}>
            {renderList('Out of reach', outOfReach, 'Move in')}
          </Col>
        </Row>
      </Card>

      <Modal
        title={modal && modal.id ? 'Edit place' : 'Add place'}
        open={Boolean(modal)}
        okText="Save"
        onOk={save}
        onCancel={() => setModal(null)}
        centered
      >
        <Form form={form} layout="vertical">
          <Form.Item name="countryCode" label="Country" rules={[{ required: true, message: 'Pick a country.' }]}>
            <Select showSearch optionFilterProp="label" options={COUNTRIES} />
          </Form.Item>
          <Form.Item name="city" label="City" extra="Leave empty for the whole country. A city overrides its country.">
            <Input placeholder="e.g. Lagos" />
          </Form.Item>
          <Form.Item name="reachable" label="Can you get there without help?">
            <Radio.Group>
              <Radio value>In reach</Radio>
              <Radio value={false}>Out of reach</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input placeholder="visa sorted, family there, too far for now…" maxLength={160} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default PlacesPage
